import { CollateralSecurityTypes, FacilityTypes } from './DealDebtSchedule';
import {
	CompanyFinancialStatementTypes,
	FacilityAmortizationPeriods,
	InterestOnlyPeriodDurations,
	PersonalGuaranteeTypes,
	TermSheetGovernanceTypes,
	TermSheetResponses,
	TermSheetRevision,
	TermSheetTypes,
	WarrantCoverageTypes,
} from './TermSheetRevision';
import { StructureTranches } from './TrancheEvaluation';

export interface TermSheet {
	id: number;
	guid: string;
	dataRoomGuid: string;
	bankDealProfileGuid: string;
	bankContactGuid: string;
	submittedOn: Date;
	dateReceived: Date;
	termsEffectiveDate?: Date;
	isIncumbentSolution: boolean;
	visibleToBorrower: boolean;
	lenderName: string;
	bankName: string;
	responseStatus: TermSheetResponses;
	defaultStructureTranche: StructureTranches;
	totalAmount: number;
	isExistingDebtTerms: boolean;
	revisions: TermSheetRevision[];
}

export interface TermSheetComparison {
	dataRoomGuid: string;
	dealGuid: string;
	companyGuid: string;
	dealName: string;
	companyName: string;
	items: ComparisonItem[];
	lineItems: ComparisonLineItem[];
}

export interface ComparisonItem {
	bankGuid: string;
	bankName: string;
	personalGuarantee: string;
	personalGuarantyDetails: string;
	requiresDeposit: boolean;
	yesDepositAmount?: number;
	yesUnusedDepositAmount?: number;
	noDepositAmount?: number;
	totalDepositAmount?: number;
	totalLoanAmount: number;
	facilities: ComparisonItemFacility[];
	fees: ComparisonItemFee[];
	covenants: ComparisonItemCovenant[];
	collateral: ComparisonItemCollateral[];
	conditions: ComparisonItemCondition[];
}

export interface ComparisonItemCollateral {
	collateralSecurityType: CollateralSecurityTypes;
	advancedRate?: number;
	availabilityLimitType: FacilityAvailabilityLimitTypes;
}

export interface ComparisonItemCondition {
	type: string;
	conditionText: string;
}

export type FacilityAvailabilityLimitTypes = keyof typeof FacilityAvailabilityLimitTypesMap;
export const FacilityAvailabilityLimitTypesMap = {
	None: 'N/A',
	EligibleAr: '% of Eligible A/R',
	MaxInventoryUsage: '% Max Usage by Inventory',
	Limit: '$ Limit',
	EligibleInventory: '% of Eligible Inventory',
	Custom: 'Custom',
};

export interface ComparisonItemFee {
	name: string;
	feeAmount: number;
	termSheetFeeType: TermSheetFeeTypes;
}

export type TermSheetFeeTypes = keyof typeof TermSheetFeeTypesMap;
export const TermSheetFeeTypesMap = {
	Currency: '$',
	Percentage: '%',
};

export interface ComparisonItemCovenant {
	covenantName: string;
	covenantTarget?: number;
	covenantTargetType: CovenantTargetTypes;
	covenantTargetDisplay: string;
}

export type CovenantValueTypes = keyof typeof CovenantValueTypesMap;
export const CovenantValueTypesMap = {
	Numeric: 'Numeric',
	Percent: 'Percent',
	Currency: 'Currency',
};

export type CovenantTargetTypes = keyof typeof CovenantTargetTypesMap;
export const CovenantTargetTypesMap = {
	GreaterThanOrEqual: 'Greater Than or Equal',
	LessThanOrEqual: 'Less Than or Equal',
	GreaterThan: 'Greater Than',
	LessThan: 'Less Than',
	Equals: 'Equals',
};
export const CovenantTargetOperatorTypesMap = {
	GreaterThanOrEqual: '>=',
	LessThanOrEqual: '<=',
	GreaterThan: '>',
	LessThan: '<',
	Equals: '=',
};

export interface ComparisonItemFacility {
	facilityType: FacilityTypes;
	isTermLoan: boolean;
	isRevolver: boolean;
	isLease: boolean;
	structureTranche: StructureTranches;
	facilityLoanAmount: number;
	highEndMaturity: number;
	highEndInterestRate: number;
	rateType: InterestRateTypes;
	interestRateType: RateTypes;
	baseRate: BaseRateTypes;
	interestCash?: number;
	baseRateFloor?: number;
	showBaseRate: boolean;
	hasGracePeriod: boolean;
	gracePeriodLength?: number;
	gracePeriodLengthType?: InterestOnlyPeriodDurations;
	amortizationPeriodLength?: number;
	ecfSweep?: number;
	gracePeriodDisplay: string;
	isInterestOnlyPeriod: boolean;
	interestOnlyPeriodDuration?: InterestOnlyPeriodDurations;
	ioPeriod?: number;
	interestOnlyPeriodDisplay: string;
}

export type RateTypes = keyof typeof RateTypesMap;
export const RateTypesMap = {
	Unknown: 'Unknown',
	Percentage: '%',
	BasisPoints: 'bps',
};

export type InterestRateTypes = keyof typeof InterestRateTypesMap;
export const InterestRateTypesMap = {
	Fixed: 'Fixed',
	Float: 'Float',
};

export type BaseRateTypes = keyof typeof BaseRateTypesMap;
export const BaseRateTypesMap = {
	Libor30Day: '30 Day Libor',
	Libor60Day: '60 Day Libor',
	Libor90Day: '90 Day Libor',
	Prime: 'Prime',
	Treasury: 'Treasury',
	Sofr: 'SOFR',
	Other: 'Other/Unknown',
};

export interface ComparisonLineItem {
	name: string;
	heading: boolean;
	subHeading: boolean;
	isSpacing: boolean;
	values: ComparisonLineItemValue[];
}

export interface ComparisonLineItemValue {
	bankGuid: string;
	value: string;
	decimalValue?: number;
	type: ComparisonLineItemValueTypes;
}

export type ComparisonLineItemValueTypes = keyof typeof ComparisonLineItemValueTypesMap;
export const ComparisonLineItemValueTypesMap = {
	Text: 'Text',
	Currency: 'Currency',
	Percent: 'Percent',
};

export interface UpdateTermSheetRevisionDto {
	isIncumbentSolution: boolean;
	visibleToBorrower: boolean;
	termSheetType: TermSheetTypes | undefined;
	hasPersonalGuaranty: boolean;
	personalGuaranteeType: PersonalGuaranteeTypes | undefined;
	personalGuarantyDetails: string;
	warrantRequired: boolean;
	warrantAmount: number | undefined;
	isWarrantAmountTbd: boolean | undefined;
	warrantCoverageType: WarrantCoverageTypes | undefined;
	warrantPrice: number | undefined;
	isWarrantPriceTbd: boolean | undefined;
	termSheetGovernanceType: TermSheetGovernanceTypes | undefined;
	numberBoardSeats: number | undefined;
	governanceDetails: string;
	warrantDetails: string;
	sourcePage: string;
	isAccepted: number;
	borrowerNotes?: string;
	optOutLenders: boolean;
	notifyOptedOutLenders: boolean;
	notifyLender: boolean;
	termsEffectiveDate?: Date | undefined;
	reportingProvided: boolean;
	covenantsProvided: boolean;
	expirationDate: Date | null;
	sendExpirationReminder?: boolean;
}

export interface TermSheetReportingFormDto {
	reportingQuality: CompanyFinancialStatementTypes | undefined;
	frequency: FacilityAmortizationPeriods | undefined;
	daysToDeliver: number | undefined;
}

export interface TermSheetConditionFormDto {
	conditionTypeId: number | undefined;
	conditionText: string | undefined;
}
